import { useCallback, useEffect, useState } from 'react';

import CopyEditor from './CopyEditor';
import CopyList from './CopyList';
import LocaleSwitch from './LocaleSwitch';
import { EditorCard, Layout } from './styles';
import useCopyList from './useCopyList';
import ConfirmationModal from '@/components/ConfirmationModalV2';
import { useLifecycle } from '../LifecycleContext';
import SectionState from '../shared/SectionState';
import { useLifecycleUrlState } from '../useLifecycleUrlState';

const CopyTab = () => {
  const { params, update } = useLifecycleUrlState<'copy'>();
  const locale = params.lang;
  const list = useCopyList(locale);

  const { setCopyDirty } = useLifecycle();
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  useEffect(() => {
    setCopyDirty(dirty);
    return () => setCopyDirty(false);
  }, [dirty, setCopyDirty]);

  const guard = useCallback(
    (action: () => void) => {
      if (dirty) setPending(() => action);
      else action();
    },
    [dirty],
  );

  const pick = useCallback(
    (message: string) =>
      guard(() => update({ message, view: 'edit' }, { replace: true })),
    [guard, update],
  );
  const setView = useCallback(
    (view: 'edit' | 'history') => update({ view }, { replace: true }),
    [update],
  );

  const setLocale = useCallback(
    (lang: 'en' | 'es') => guard(() => update({ lang }, { replace: true })),
    [guard, update],
  );

  return (
    <>
      <LocaleSwitch locale={locale} onChange={setLocale} />
      <Layout>
        <CopyList
          items={list.items}
          loading={list.loading}
          error={list.error}
          selected={params.message}
          onPick={pick}
          onRetry={list.retry}
        />
        {params.message ? (
          <CopyEditor
            key={`${params.message}/${locale}`}
            stepKey={params.message}
            locale={locale}
            view={params.view}
            onView={setView}
            onSaved={list.reload}
            onDirty={setDirty}
          />
        ) : (
          <EditorCard data-testid="copyEditor" aria-label="Copy editor">
            <SectionState
              mode="empty"
              title="Pick a message"
              caption="Choose a message from the list to read and edit its text."
            />
          </EditorCard>
        )}
      </Layout>
      {pending && (
        <ConfirmationModal
          disableClickout
          title="Discard unsaved changes?"
          message="Your edits to this message are not saved."
          subMessage={null}
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            pending();
            setPending(null);
          }}
        />
      )}
    </>
  );
};

export default CopyTab;
